import { FunctionComponent, useEffect, useState } from 'react'
import { Daycare } from '@/types/Daycare'
import { Role } from '@/types/Role'
import { GridColDef, GridRowClassNameParams } from '@mui/x-data-grid'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import Groups from '@mui/icons-material/Groups'
import { useTranslation } from 'react-i18next'
import styles from './daycaresTable.module.scss'
import Check from '@mui/icons-material/Check'
import Close from '@mui/icons-material/Close'
import ContentCopy from '@mui/icons-material/ContentCopy'
import LinkOff from '@mui/icons-material/LinkOff'
import moment from 'moment-timezone'
import Desktop from '@/modules/desktopMobile/desktop'
import Mobile from '@/modules/desktopMobile/mobile'
import { frFR } from '@mui/x-data-grid/locales'
import StaffListDialog from './Dialog/staffListDialog'
import EditDaycareDialog from './Dialog/editDaycareDialog'
import { daycareTypeToString } from '@/modules/helper/daycareHelper'
import { QuickSearchToolbar, StyledDataGrid } from '@/components/_custom/DataGrid'
import Indicator from '@/components/_custom/Indicator/indicator'
import { useLazyQuery } from '@apollo/client'
import ConfirmUnlinkDaycareDialog from './Dialog/confirmUnlinkDaycareDialog'
import {
    GET_DAYCARE_WITHOUT_FAMILY,
    GET_DAYCARE_WITHOUT_SLOTS,
    GET_DAYCARE_WITHOUT_SLOTS_2_MONTHS
} from '@/modules/API/daycare/daycareHandler'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { Contacts } from '@mui/icons-material'

const annuaireUrl: string | undefined = import.meta.env.VITE_ANNUAIRE_URL

interface daycaresTableProps {
    daycares: Array<Daycare>
    role: Role | undefined
    refreshDaycares: () => void
    unlinkDaycare?: boolean
}

const DaycaresTable: FunctionComponent<daycaresTableProps> = ({
    daycares,
    role,
    refreshDaycares,
    unlinkDaycare = false
}: daycaresTableProps) => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const { showNotificationSuccess } = useNotification()

    const headerClassName: string = role === Role.parent ? styles.HeaderParent : styles.HeaderDaycare

    const daycareColumnsCALDWeb: GridColDef[] = [
        {
            field: 'city',
            headerName: t('city'),
            headerClassName: headerClassName,
            width: 160,
            minWidth: 160,
            maxWidth: 300,
            flex: 1
        },
        {
            field: 'zip',
            headerName: t('zipCode'),
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 100,
            minWidth: 100,
            flex: 1,
            align: 'center'
        },
        {
            field: 'type',
            headerName: t('type'),
            headerClassName: headerClassName,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>{daycareTypeToString(params.row.type, t, true)}</div>
            )
        },
        {
            field: 'name',
            headerName: t('name'),
            headerClassName: headerClassName,
            width: 160,
            minWidth: 160,
            maxWidth: 300,
            flex: 1,
            renderCell: (params) => (
                <a
                    className={`${styles.InfoContainer} ${styles.Clickable}`}
                    href={`/daycare?daycare=${params.row.id}`}
                    onClick={(e) => {
                        if (!(e.ctrlKey || e.metaKey)) {
                            e.preventDefault()
                            navigate(`/daycare?daycare=${params.row.id}`)
                        }
                    }}
                >
                    {params.row.name}
                </a>
            )
        },
        {
            field: 'phone',
            headerName: t('phone'),
            headerClassName: headerClassName,
            width: 160,
            maxWidth: 160,
            minWidth: 160,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.phone ? (
                        <a href={`tel:${params.row.phone}`}>{params.row.phone.match(/.{1,2}/g).join(' ')}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'email',
            headerName: t('email'),
            headerClassName: headerClassName,
            width: 400,
            maxWidth: 800,
            minWidth: 250,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.email ? (
                        <>
                            {role === Role.cald && (
                                <ContentCopy className={styles.Icon} onClick={() => copyEmail(params.row.email)} />
                            )}
                            <a href={`mailto:${params.row.email}`}>{params.row.email}</a>
                        </>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'formattedValidityEnd',
            headerName: t('validity'),
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 100,
            minWidth: 100,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.formattedValidityEnd ? (
                        <p
                            className={
                                moment().isAfter(moment(params.row.formattedValidityEnd, 'YYYY-MM-DD'))
                                    ? styles.RedText
                                    : ''
                            }
                        >
                            {moment(params.row.formattedValidityEnd, 'YYYY-MM-DD').format('DD/MM/YYYY')}
                        </p>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'staffAccess',
            headerName: 'St - Ac - Su - Ca - Re',
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 200,
            minWidth: 100,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.CheckContainer}>
                    {params.row.staffAccess ? (
                        <Check className={styles.GreenIcon} />
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                    {params.row.newsAccess ? (
                        <Check className={styles.GreenIcon} />
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                    {params.row.suggestAccess ? (
                        <Check className={styles.GreenIcon} />
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                    {params.row.taskNoteAccess ? (
                        <Check className={styles.GreenIcon} />
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                    {params.row.mealAccess ? (
                        <Check className={styles.GreenIcon} />
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'staffCount',
            headerName: role === Role.cald ? t('staffCount') : t('staffs'),
            headerClassName: headerClassName,
            width: 80,
            maxWidth: 80,
            minWidth: 50,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div
                    className={styles.IconContainer}
                    onClick={() => openStaffList(params.row.id, params.row.staffCount)}
                >
                    {params.row.staffCount}
                    <Groups className={styles.Icon} />
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 300,
            minWidth: 100,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    {params.row.annuaireId && (
                        <a
                            href={annuaireUrl + `/daycare/${params.row.annuaireId}`}
                            className={styles.Link}
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            <Contacts className={styles.Icon} />
                        </a>
                    )}
                    <EditIcon onClick={() => openDaycareEdit(params.row)} className={styles.Icon} />
                    {unlinkDaycare && <LinkOff onClick={() => unlinkOneDaycare(params.row)} className={styles.Icon} />}
                    <a
                        href={`/daycare?daycare=${params.row.id}`}
                        onClick={(e) => {
                            if (!(e.ctrlKey || e.metaKey)) {
                                e.preventDefault()
                                navigate(`/daycare?daycare=${params.row.id}`)
                            }
                        }}
                        className={styles.Link}
                    >
                        <VisibilityIcon className={styles.Icon} />
                    </a>
                </div>
            )
        }
    ]

    const daycareColumnsWeb: GridColDef[] = [
        {
            field: 'city',
            headerName: t('city'),
            headerClassName: headerClassName,
            width: 200,
            minWidth: 200,
            maxWidth: 400,
            flex: 1
        },
        {
            field: 'zip',
            headerName: t('zipCode'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 250,
            minWidth: 150,
            flex: 1,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'name',
            headerName: t('name'),
            headerClassName: headerClassName,
            width: 200,
            minWidth: 200,
            maxWidth: 400,
            flex: 1,
            renderCell: (params) => (
                <div
                    className={`${styles.InfoContainer} ${styles.Clickable}`}
                    onClick={() => navigate(`/daycare?daycare=${params.row.id}`)}
                >
                    {params.row.name}
                </div>
            )
        },
        {
            field: 'phone',
            headerName: t('phone'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 250,
            minWidth: 100,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.phone ? (
                        <a href={`tel:${params.row.phone}`}>{params.row.phone.match(/.{1,2}/g).join(' ')}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'email',
            headerName: t('email'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 500,
            minWidth: 200,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.email ? (
                        <a href={`mailto:${params.row.email}`}>{params.row.email}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'staffCount',
            headerName: role === Role.cald ? t('staffCount') : t('staffs'),
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 150,
            minWidth: 50,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div
                    className={styles.IconContainer}
                    onClick={() => openStaffList(params.row.id, params.row.staffCount)}
                >
                    {params.row.staffCount}
                    <Groups className={styles.Icon} />
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 200,
            minWidth: 100,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <VisibilityIcon
                        onClick={() => navigate(`/daycare?daycare=${params.row.id}`)}
                        className={styles.Icon}
                    />
                </div>
            )
        }
    ]

    const daycareParentColumnsWeb: GridColDef[] = [
        {
            field: 'city',
            headerName: t('city'),
            headerClassName: headerClassName,
            width: 200,
            minWidth: 200,
            maxWidth: 400,
            flex: 1
        },
        {
            field: 'zip',
            headerName: t('zipCode'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 250,
            minWidth: 150,
            flex: 1,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'name',
            headerName: t('name'),
            headerClassName: headerClassName,
            width: 200,
            minWidth: 200,
            maxWidth: 400,
            flex: 1,
            renderCell: (params) => (
                <div
                    className={`${styles.InfoContainer} ${styles.Clickable}`}
                    onClick={() => navigate(`/daycare?daycare=${params.row.id}`)}
                >
                    {params.row.name}
                </div>
            )
        },
        {
            field: 'phone',
            headerName: t('phone'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 250,
            minWidth: 100,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.phone ? (
                        <a href={`tel:${params.row.phone}`}>{params.row.phone.match(/.{1,2}/g).join(' ')}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'email',
            headerName: t('email'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 500,
            minWidth: 200,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.email ? (
                        <a href={`mailto:${params.row.email}`}>{params.row.email}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 200,
            minWidth: 100,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <VisibilityIcon
                        onClick={() => navigate(`/daycare?daycare=${params.row.id}`)}
                        className={styles.Icon}
                    />
                </div>
            )
        }
    ]

    const daycareColumnsMobile: GridColDef[] = [
        {
            field: 'city',
            headerName: t('city'),
            headerClassName: headerClassName,
            flex: 1
        },
        {
            field: 'name',
            headerName: t('name'),
            headerClassName: headerClassName,
            flex: 1,
            renderCell: (params) => (
                <div
                    className={`${styles.InfoContainer} ${styles.Clickable}`}
                    onClick={() => navigate(`/daycare?daycare=${params.row.id}`)}
                >
                    {params.row.name}
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    {role === Role.cald && (
                        <>
                            <EditIcon onClick={() => openDaycareEdit(params.row)} className={styles.Icon} />
                            {unlinkDaycare && (
                                <LinkOff onClick={() => unlinkOneDaycare(params.row)} className={styles.Icon} />
                            )}
                        </>
                    )}
                    <VisibilityIcon
                        onClick={() => navigate(`/daycare?daycare=${params.row.id}`)}
                        className={styles.Icon}
                    />
                </div>
            ),
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center'
        }
    ]

    const [isStaffDialogOpen, setStaffDialogOpen] = useState(false)
    const [showStaffDaycareId, setShowStaffDaycareId] = useState<number>(0)

    const [isEditDaycareDialogOpen, setEditDaycareDialogOpen] = useState(false)
    const [isUnlinkDaycareDialogOpen, setUnlinkDaycareDialogOpen] = useState(false)
    const [daycareToEdit, setDaycareToEdit] = useState<Daycare | null>(null)

    function copyEmail(email: string): void {
        navigator.clipboard.writeText(email).then(() => {
            showNotificationSuccess(t('emailCopied'))
        })
    }

    function openStaffList(daycareId: number, staffCount: number): void {
        if (staffCount > 0) {
            setShowStaffDaycareId(daycareId)
            setStaffDialogOpen(true)
        } else {
            showNotificationSuccess(t('noStaff'))
        }
    }

    function openDaycareEdit(daycare: Daycare): void {
        if (daycare) {
            setDaycareToEdit(daycare)
            setEditDaycareDialogOpen(true)
        } else {
            showNotificationSuccess(t('noDaycare'))
        }
    }

    function unlinkOneDaycare(daycare: Daycare): void {
        if (daycare) {
            setDaycareToEdit(daycare)
            setUnlinkDaycareDialogOpen(true)
        } else {
            showNotificationSuccess(t('noDaycare'))
        }
    }

    const [daycareData, setDaycareData] = useState<Daycare[]>(daycares)

    const [daycareWithoutFamily, setDaycareWithoutFamily] = useState<number>(0)
    const [daycareIdWithoutFamily, setDaycareIdWithoutFamily] = useState<Array<number>>([])

    const [daycareWithoutSlots, setDaycareWithoutSlots] = useState<number>(0)
    const [daycareIdWithoutSlots, setDaycareIdWithoutSlots] = useState<Array<number>>([])

    const [daycareWithoutSlots2Months, setDaycareWithoutSlots2Months] = useState<number>(0)
    const [daycareIdWithoutSlots2Months, setDaycareIdWithoutSlots2Months] = useState<Array<number>>([])

    useEffect(() => {
        setDaycareWithoutFamily(0)
        setDaycareIdWithoutFamily([])

        setDaycareData(role === Role.cald ? daycares.filter((daycare: Daycare) => daycare.active) : daycares)

        getDaycareWithoutFamily()
        getDaycareWithoutSlots()
        if (role === Role.cald) getDaycareWithoutSlots2Months()
    }, [daycares])

    const [getDaycareWithoutFamily, { data: dataDaycareWithoutFamily }] = useLazyQuery(GET_DAYCARE_WITHOUT_FAMILY)

    useEffect(() => {
        if (dataDaycareWithoutFamily) {
            setDaycareWithoutFamily(dataDaycareWithoutFamily.getDaycareWithoutFamily.length)
            setDaycareIdWithoutFamily(dataDaycareWithoutFamily.getDaycareWithoutFamily)
        }
    }, [dataDaycareWithoutFamily])

    const [getDaycareWithoutSlots, { data: dataDaycareWithoutSlots }] = useLazyQuery(GET_DAYCARE_WITHOUT_SLOTS)

    useEffect(() => {
        if (dataDaycareWithoutSlots) {
            setDaycareWithoutSlots(dataDaycareWithoutSlots.getDaycareWithoutSlots.length)
            setDaycareIdWithoutSlots(dataDaycareWithoutSlots.getDaycareWithoutSlots)
        }
    }, [dataDaycareWithoutSlots])

    const [getDaycareWithoutSlots2Months, { data: dataDaycareWithoutSlots2Months }] = useLazyQuery(
        GET_DAYCARE_WITHOUT_SLOTS_2_MONTHS
    )

    useEffect(() => {
        if (dataDaycareWithoutSlots2Months) {
            setDaycareWithoutSlots2Months(dataDaycareWithoutSlots2Months.getDaycareWithoutSlots2Months.length)
            setDaycareIdWithoutSlots2Months(dataDaycareWithoutSlots2Months.getDaycareWithoutSlots2Months)
        }
    }, [dataDaycareWithoutSlots2Months])

    function resetFilter(): void {
        setDaycareData(daycares)
    }

    function activeDaycareFilter(): void {
        const activeDaycares: Daycare[] = daycares.filter((daycare: Daycare) => daycare.active)
        setDaycareData(activeDaycares)
    }

    function expirationFilter(): void {
        const expirationDaycares: Daycare[] = daycares.filter(
            (daycare: Daycare) =>
                moment(daycare.validityEnd, 'DD-MM-YYYY').isBefore(moment().add(3, 'month')) && daycare.active
        )
        setDaycareData(expirationDaycares)
    }

    function withoutFamilyFilter(): void {
        const withoutFamilyDaycares: Daycare[] = daycares.filter((daycare: Daycare) =>
            daycareIdWithoutFamily.includes(daycare.id)
        )
        setDaycareData(withoutFamilyDaycares)
    }

    function withoutSlotsFilter(): void {
        const withoutSlotsDaycares: Daycare[] = daycares.filter((daycare: Daycare) =>
            daycareIdWithoutSlots.includes(daycare.id)
        )
        setDaycareData(withoutSlotsDaycares)
    }

    function withoutSlots2MonthsFilter(): void {
        const withoutSlots2MonthsDaycares: Daycare[] = daycares.filter((daycare: Daycare) =>
            daycareIdWithoutSlots2Months.includes(daycare.id)
        )
        setDaycareData(withoutSlots2MonthsDaycares)
    }

    return (
        <>
            {role !== Role.parent && (
                <div className={styles.IndicatorsContainer}>
                    <a onClick={resetFilter} className={styles.Clickable}>
                        <Indicator count={daycares.length} text={t('structures')} />
                    </a>
                    {role === Role.cald && (
                        <>
                            <a onClick={activeDaycareFilter} className={styles.Clickable}>
                                <Indicator
                                    count={daycares.filter((daycare) => daycare.active).length}
                                    text={
                                        daycares.filter((daycare) => daycare.active).length > 1
                                            ? t('daycaresActive')
                                            : t('daycareActive')
                                    }
                                />
                            </a>
                            <a onClick={expirationFilter} className={styles.Clickable}>
                                <Indicator
                                    count={
                                        daycares.filter(
                                            (daycare) =>
                                                moment(daycare.validityEnd, 'DD-MM-YYYY').isBefore(
                                                    moment().add(3, 'month')
                                                ) && daycare.active
                                        ).length
                                    }
                                    text={
                                        daycares.filter(
                                            (daycare) =>
                                                moment(daycare.validityEnd, 'DD-MM-YYYY').isBefore(
                                                    moment().add(3, 'month')
                                                ) && daycare.active
                                        ).length > 1
                                            ? t('expirations')
                                            : t('expiration')
                                    }
                                />
                            </a>
                        </>
                    )}
                    {!unlinkDaycare && (
                        <>
                            <a onClick={withoutFamilyFilter} className={styles.Clickable}>
                                <Indicator count={daycareWithoutFamily} text={t('withoutFamily')} />
                            </a>
                            <a onClick={withoutSlotsFilter} className={styles.Clickable}>
                                <Indicator count={daycareWithoutSlots} text={t('withoutSlot')} />
                            </a>
                            {role === Role.cald && (
                                <a onClick={withoutSlots2MonthsFilter} className={styles.Clickable}>
                                    <Indicator count={daycareWithoutSlots2Months} text={t('withoutSlot2Months')} />
                                </a>
                            )}
                        </>
                    )}
                </div>
            )}
            {role === Role.cald ? (
                <>
                    <Desktop>
                        <StyledDataGrid
                            autoHeight
                            columns={daycareColumnsCALDWeb}
                            rows={daycareData}
                            disableDensitySelector
                            disableColumnMenu
                            slots={{ toolbar: QuickSearchToolbar }}
                            showToolbar
                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                            rowHeight={80}
                            className={styles.DataGrid}
                            getRowClassName={(params: GridRowClassNameParams): string => {
                                return `daycare-active-${params.row.active}`
                            }}
                            paginationMode={'server'}
                            rowCount={daycareData.length}
                        />
                    </Desktop>
                    <Mobile>
                        <StyledDataGrid
                            autoHeight
                            columns={daycareColumnsMobile}
                            rows={daycareData}
                            disableDensitySelector
                            disableColumnMenu
                            slots={{ toolbar: QuickSearchToolbar }}
                            showToolbar
                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                            rowHeight={80}
                            className={styles.DataGrid}
                            paginationMode={'server'}
                            rowCount={daycareData.length}
                        />
                    </Mobile>
                </>
            ) : role === Role.parent ? (
                <>
                    <Desktop>
                        <StyledDataGrid
                            autoHeight
                            columns={daycareParentColumnsWeb}
                            rows={daycareData}
                            disableDensitySelector
                            disableColumnMenu
                            slots={{ toolbar: QuickSearchToolbar }}
                            showToolbar
                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                            rowHeight={80}
                            className={styles.DataGrid}
                            paginationMode={'server'}
                            rowCount={daycareData.length}
                        />
                    </Desktop>
                    <Mobile>
                        <StyledDataGrid
                            autoHeight
                            columns={daycareColumnsMobile}
                            rows={daycareData}
                            disableDensitySelector
                            disableColumnMenu
                            slots={{ toolbar: QuickSearchToolbar }}
                            showToolbar
                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                            rowHeight={80}
                            className={styles.DataGrid}
                            paginationMode={'server'}
                            rowCount={daycareData.length}
                        />
                    </Mobile>
                </>
            ) : (
                <>
                    <Desktop>
                        <StyledDataGrid
                            autoHeight
                            columns={daycareColumnsWeb}
                            rows={daycareData}
                            disableDensitySelector
                            disableColumnMenu
                            slots={{ toolbar: QuickSearchToolbar }}
                            showToolbar
                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                            rowHeight={80}
                            className={styles.DataGrid}
                            paginationMode={'server'}
                            rowCount={daycareData.length}
                        />
                    </Desktop>
                    <Mobile>
                        <StyledDataGrid
                            autoHeight
                            columns={daycareColumnsMobile}
                            rows={daycareData}
                            disableDensitySelector
                            disableColumnMenu
                            slots={{ toolbar: QuickSearchToolbar }}
                            showToolbar
                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                            rowHeight={80}
                            className={styles.DataGrid}
                            paginationMode={'server'}
                            rowCount={daycareData.length}
                        />
                    </Mobile>
                </>
            )}
            <StaffListDialog
                isDialogOpen={isStaffDialogOpen}
                handleCloseDialog={() => setStaffDialogOpen(false)}
                showStaffDaycareId={showStaffDaycareId}
            />
            <EditDaycareDialog
                isDialogOpen={isEditDaycareDialogOpen}
                handleCloseDialog={() => setEditDaycareDialogOpen(false)}
                daycare={daycareToEdit}
                refreshDaycares={refreshDaycares}
            />
            <ConfirmUnlinkDaycareDialog
                open={isUnlinkDaycareDialogOpen}
                handleClose={() => setUnlinkDaycareDialogOpen(false)}
                daycare={daycareToEdit}
                refetchGroups={refreshDaycares}
            />
        </>
    )
}

export default DaycaresTable
